import { OpenInNew } from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import type React from "react";
import { useRef, useState } from "react";
import ReactPlayer from "react-player";

type VideoPlayerProps = {
  src: string;
  width?: string | number;
  height?: string | number;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  pageUrl?: string;
  title?: string;
  playFrom?: number; // Time in seconds to seek to
  playbackRate?: number;
  onReady?: () => void;
  onLoadedData?: React.ReactEventHandler<HTMLVideoElement>;
};

const initialState = {
  pip: false,
  playing: false,
  controls: false,
  light: false,
  volume: 1,
  muted: false,
  played: 0,
  loaded: 0,
  duration: 0,
  playbackRate: 1.0,
  loop: false,
  seeking: false,
  loadedSeconds: 0,
  playedSeconds: 0,
};

type PlayerState = typeof initialState;

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  width = "100%",
  height = "520px",
  controls = true,
  autoPlay = false,
  muted = true,
  loop = false,
  playbackRate = 1.0,
  pageUrl,
  title,
  onReady,
  onLoadedData,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [state, setState] = useState<PlayerState>(initialState);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerRef = useRef<any>(null);

  const handleReady = () => {
    setState((prev) => ({ ...prev, playing: autoPlay }));
    if (onReady) {
      onReady();
    }
  };

  const videoComponent = (
    <ReactPlayer
      ref={playerRef}
      src={src}
      width="100%"
      height="100%"
      controls={controls}
      playing={state.playing}
      muted={muted}
      loop={loop}
      playbackRate={playbackRate}
      onReady={handleReady}
      onLoadedData={onLoadedData}
      style={{
        border: "3px solid black",
        borderRadius: "8px",
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
      }}
    />
  );

  const content = (
    <Box
      sx={{
        position: "relative",
        width,
        height,
      }}
    >
      {/* Video Content Area */}
      <Box
        sx={{ position: "relative", width: "100%", height: "100%" }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {videoComponent}

        {/* Title overlay on hover */}
        {isHovered && title && (
          <Tooltip title={title} placement="bottom-start">
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                right: pageUrl ? 60 : 8, // Leave space for the external link button if present
                zIndex: 2,
                backgroundColor: "rgba(0, 0, 0, 0.85)",
                color: "white",
                p: 1.5,
                borderRadius: "6px",
                backdropFilter: "blur(4px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                width: "fit-content",
                maxWidth: pageUrl ? "calc(100% - 68px)" : "calc(100% - 16px)",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  wordWrap: "break-word",
                  lineHeight: 1.3,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </Typography>
            </Box>
          </Tooltip>
        )}

        {/* Optional external link overlay */}
        {pageUrl && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          >
            <Tooltip title="Open in new tab">
              <IconButton
                size="small"
                onClick={() => window.open(pageUrl, "_blank")}
                sx={{
                  backgroundColor: "rgba(0,0,0,0.6)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.8)",
                  },
                }}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );

  return content;
};
